import { AllocationRiskProfile } from '../../../expert-review/types/allocation.types'
import { getFundLogoPath, MockFundAmc } from '../../../../../data/mockUtils'
import { SipPlansPlanType, SipPlanStatus, SipRecommendationStatus } from '../../../plans-v4/sip/api/common.types'
import type {
  SipIdealSip,
  SipIncludedFund,
  SipPlansOverviewCategory,
  SipPlansOverviewResponse
} from '../../../plans-v4/sip/api/overview.types'

import { PLAN_MOCK_FUNDS } from '../shared/planMockFunds'
import type { SipPlanScoringData } from '../../../plans-v4/sip/plan-bottom-sheet/api/sipPlanRecommendation.types'
import { SipCase } from '../../../plans-v4/sip/plan-bottom-sheet/api/sipPlanRecommendation.types'
import { deriveRecommendedSipAmount } from '../../../plans-v4/sip/plans-list/deriveSipPlansView'

export enum SipPlansMockScenario {
  NORMAL = 'normal',
  UNDER_REVIEW_ONLY = 'under_review_only',
  EMPTY = 'empty',
  WITH_TAGS = 'with_tags'
}

export type SipMockYourPlanCounts = {
  stop: number
  start: number
  increase: number
  decrease: number
  noAction: number
}

export type SipPlanGateMockConfig = {
  planStatus: SipPlanStatus
  isPlanSubscriptionEligible: boolean
  daysToPlanDrop: number
  /** Rolled-out SIPs whose current amount already matches the recommended amount. */
  isTopShape: boolean
  yourPlanCounts: SipMockYourPlanCounts
}

const DEFAULT_SIP_PLAN_GATE: SipPlanGateMockConfig = {
  planStatus: SipPlanStatus.PLAN_ROLLED_OUT,
  isPlanSubscriptionEligible: true,
  daysToPlanDrop: 7,
  isTopShape: false,
  yourPlanCounts: {
    stop: 0,
    start: 0,
    increase: 0,
    decrease: 0,
    noAction: 0
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

const MOCK_STRATEGY_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/allocation/strategy-questionnaire',
  isNavigationDeferred: false
} as const

const MOCK_RESET_ALLOCATION_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'POST',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/allocation/reset-allocation',
  isNavigationDeferred: false
} as const

const MOCK_EDIT_SIP_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/portfolio/allocation-plan/sip/edit-external',
  isNavigationDeferred: false
} as const

const MOCK_STRATEGY_LANDING_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/allocation-strategy/landing'
} as const

const MOCK_SLOT_BOTTOMSHEET_CTA = {
  refreshScreen: false,
  url: '/v2/allocation-strategy/slot-bottom-sheet?planType=SIP&group=placeholderGroup&category=placeholderCategory',
  method: 'GET'
} as const

function resolveMockTimeToPlanDrop(daysToPlanDrop: number): number {
  return Date.now() + daysToPlanDrop * DAY_MS
}

function resolveListScenarioForPlanStatus(
  planStatus: SipPlanStatus,
  scenario: SipPlansMockScenario
): SipPlansMockScenario {
  if (planStatus !== SipPlanStatus.PLAN_ROLLED_OUT) {
    return SipPlansMockScenario.EMPTY
  }
  return scenario
}

const MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT = {
  NEW: 'New: Fund added to plan',
  UPDATED_STOP: 'Updated: Better alternate fund found',
  UPDATED_DECREASE: 'Updated: Plan revised for better fit',
  UPDATED_INCREASE: 'Updated: Plan revised for better fit',
  SAME: 'Same as Previous Plan'
} as const

const MOCK_INITIATE_SIP_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'POST',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/execution/sip',
  isNavigationDeferred: false
} as const

const MOCK_MANAGE_SIP_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'POST',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/execution/manage-sip',
  isNavigationDeferred: false
} as const

let mockPersistedTotalIdealSipAmount: number | null = null

type SipFolioSource = 'powerup' | 'external' | 'infinite'

type SipFolioAmountsInput = {
  powerup?: number | true
  external?: number | true
  infinite?: number | true
}

type SipAmountFields = Pick<
  SipIncludedFund,
  'currentSipAmount' | 'powerupSipAmount' | 'externalSipAmount' | 'infiniteSipAmount'
>

const SIP_FOLIO_SOURCES: SipFolioSource[] = ['powerup', 'external', 'infinite']

function resolveFolioAmount(value: number | true | undefined, currentSipAmount: number): number {
  if (value === true) {
    return currentSipAmount
  }
  if (value === undefined) {
    return 0
  }
  return value
}

/** Builds current + folio SIP amounts; unspecified folio slots absorb the remainder. */
function sipAmounts(currentSipAmount: number, folio: SipFolioAmountsInput = {}): SipAmountFields {
  const specifiedSources = SIP_FOLIO_SOURCES.filter((source) => folio[source] !== undefined)

  if (specifiedSources.length === 0) {
    return {
      currentSipAmount,
      powerupSipAmount: currentSipAmount,
      externalSipAmount: 0,
      infiniteSipAmount: 0
    }
  }

  let powerupSipAmount = resolveFolioAmount(folio.powerup, currentSipAmount)
  let externalSipAmount = resolveFolioAmount(folio.external, currentSipAmount)
  let infiniteSipAmount = resolveFolioAmount(folio.infinite, currentSipAmount)

  const remainder = currentSipAmount - (powerupSipAmount + externalSipAmount + infiniteSipAmount)

  if (Math.abs(remainder) > 0.0001) {
    const lastSpecifiedSource = specifiedSources[specifiedSources.length - 1]
    const lastSpecifiedIndex = SIP_FOLIO_SOURCES.indexOf(lastSpecifiedSource)
    const remainderTarget =
      SIP_FOLIO_SOURCES.slice(lastSpecifiedIndex + 1).find((source) => folio[source] === undefined) ??
      lastSpecifiedSource

    if (remainderTarget === 'powerup') {
      powerupSipAmount += remainder
    } else if (remainderTarget === 'external') {
      externalSipAmount += remainder
    } else {
      infiniteSipAmount += remainder
    }
  }

  return {
    currentSipAmount,
    powerupSipAmount,
    externalSipAmount,
    infiniteSipAmount
  }
}

function mockIncludedFund(amc: MockFundAmc, fund: Omit<SipIncludedFund, 'mfLogoUrl'>): SipIncludedFund {
  return {
    fundNature: 'Equity',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    taxCategory: 'EQUITY',
    powerRank: 2,
    powerRating: 'IN_FORM',
    currentSipAmount: 0,
    powerupSipAmount: 0,
    externalSipAmount: 0,
    infiniteSipAmount: 0,
    ...fund,
    mfLogoUrl: getFundLogoPath(amc)
  }
}

type SipMockCountFund = {
  amc: MockFundAmc
  isin: string
  fundName: string
  categoryId: string
  categoryName: string
  fundNature: string
}

/** Name matches the AMC passed to the logo. Counts walk this list across categories, then stop. */
const SIP_MOCK_COUNT_CATEGORIES: SipMockCountCategory[] = [
  {
    categoryId: 'LARGE_CAP',
    categoryName: 'Large Cap',
    fundNature: 'Equity',
    funds: [
      { amc: MockFundAmc.HDFC, isin: 'INF179K01LC01', fundName: 'HDFC Large Cap Fund' },
      { amc: MockFundAmc.ICICI_PRUDENTIAL, isin: 'INF109K01LC02', fundName: 'ICICI Prudential Bluechip Fund' },
      { amc: MockFundAmc.SBI, isin: 'INF200K01LC03', fundName: 'SBI Bluechip Fund' },
      { amc: MockFundAmc.AXIS, isin: 'INF846K01LC04', fundName: 'Axis Bluechip Fund' }
    ]
  },
  {
    categoryId: 'MID_CAP',
    categoryName: 'Mid Cap',
    fundNature: 'Equity',
    funds: [
      { amc: MockFundAmc.HDFC, isin: 'INF179K01MC01', fundName: 'HDFC Mid-Cap Opportunities Fund' },
      { amc: MockFundAmc.KOTAK_MAHINDRA, isin: 'INF174K01MC02', fundName: 'Kotak Emerging Equity Fund' },
      { amc: MockFundAmc.AXIS, isin: 'INF846K01MC03', fundName: 'Axis Midcap Fund' },
      { amc: MockFundAmc.NIPPON_LIFE_INDIA, isin: 'INF204K01MC04', fundName: 'Nippon India Growth Fund' }
    ]
  },
  {
    categoryId: 'SMALL_CAP',
    categoryName: 'Small Cap',
    fundNature: 'Equity',
    funds: [
      { amc: MockFundAmc.NIPPON_LIFE_INDIA, isin: 'INF204K01SC01', fundName: 'Nippon India Small Cap Fund' },
      { amc: MockFundAmc.SBI, isin: 'INF200K01SC02', fundName: 'SBI Small Cap Fund' },
      { amc: MockFundAmc.AXIS, isin: 'INF846K01SC03', fundName: 'Axis Small Cap Fund' },
      { amc: MockFundAmc.HDFC, isin: 'INF179K01SC04', fundName: 'HDFC Small Cap Fund' }
    ]
  },
  {
    categoryId: 'DEBT',
    categoryName: 'Debt',
    fundNature: 'Debt',
    funds: [
      { amc: MockFundAmc.HDFC, isin: 'INF179K01DB01', fundName: 'HDFC Short Term Debt Fund' },
      { amc: MockFundAmc.ICICI_PRUDENTIAL, isin: 'INF109K01DB02', fundName: 'ICICI Prudential Short Term Fund' },
      { amc: MockFundAmc.SBI, isin: 'INF200K01DB03', fundName: 'SBI Short Term Debt Fund' },
      { amc: MockFundAmc.AXIS, isin: 'INF846K01DB04', fundName: 'Axis Short Term Fund' }
    ]
  },
  {
    categoryId: 'HYBRID',
    categoryName: 'Hybrid',
    fundNature: 'Hybrid',
    funds: [
      { amc: MockFundAmc.HDFC, isin: 'INF179K01HY01', fundName: 'HDFC Balanced Advantage Fund' },
      { amc: MockFundAmc.ICICI_PRUDENTIAL, isin: 'INF109K01HY02', fundName: 'ICICI Prudential Equity & Debt Fund' },
      { amc: MockFundAmc.SBI, isin: 'INF200K01HY03', fundName: 'SBI Equity Hybrid Fund' },
      { amc: MockFundAmc.AXIS, isin: 'INF846K01HY04', fundName: 'Axis Aggressive Hybrid Fund' }
    ]
  },
  {
    categoryId: 'INTERNATIONAL',
    categoryName: 'International',
    fundNature: 'Equity',
    funds: [
      { amc: MockFundAmc.MOTILAL_OSWAL, isin: 'INF247L01IN01', fundName: 'Motilal Oswal Nasdaq 100 Fund of Fund' },
      { amc: MockFundAmc.NIPPON_LIFE_INDIA, isin: 'INF204K01IN02', fundName: 'Nippon India US Equity Opportunities Fund' },
      { amc: MockFundAmc.FRANKLIN_TEMPLETON, isin: 'INF090I01IN03', fundName: 'Franklin India Feeder Franklin US Opportunities Fund' },
      { amc: MockFundAmc.MIRAE, isin: 'INF769K01IN04', fundName: 'Mirae Asset NYSE FANG+ ETF Fund of Fund' }
    ]
  }
]

const SIP_MOCK_COUNT_CATEGORY_ORDER = SIP_MOCK_COUNT_CATEGORIES.map((category) => ({
  categoryId: category.categoryId,
  categoryName: category.categoryName
}))

const SIP_MOCK_COUNT_FUNDS: SipMockCountFund[] = roundRobinSipMockCountFunds(SIP_MOCK_COUNT_CATEGORIES)

const SIP_MOCK_STOP_SIP_AMOUNTS = [10000, 15000, 8000, 20000]

function buildSipYourPlanCountCategories(
  totalIdealSipAmount: number,
  yourPlanCounts: SipMockYourPlanCounts
): SipPlansOverviewCategory[] {
  let nextFundIndex = 0
  const takeFunds = (count: number) => {
    const taken = SIP_MOCK_COUNT_FUNDS.slice(nextFundIndex, nextFundIndex + count)
    nextFundIndex += taken.length
    return taken
  }

  const stopFunds = takeFunds(yourPlanCounts.stop)
  const idealPlacements = [
    { action: 'start' as const, funds: takeFunds(yourPlanCounts.start) },
    { action: 'increase' as const, funds: takeFunds(yourPlanCounts.increase) },
    { action: 'decrease' as const, funds: takeFunds(yourPlanCounts.decrease) },
    { action: 'noAction' as const, funds: takeFunds(yourPlanCounts.noAction) }
  ]
  const idealFundCount = idealPlacements.reduce((total, placement) => total + placement.funds.length, 0)
  const idealPercents = sipMockIdealPercents(idealFundCount)
  let idealFundIndex = 0

  const includedByCategory = new Map<string, SipIncludedFund[]>()
  const idealByCategory = new Map<string, SipIdealSip[]>()

  for (const [stopIndex, stopFund] of stopFunds.entries()) {
    const currentSipAmount = SIP_MOCK_STOP_SIP_AMOUNTS[stopIndex % SIP_MOCK_STOP_SIP_AMOUNTS.length]
    pushSipMockCategoryFund(includedByCategory, stopFund.categoryId, mockCountedIncludedFund(stopFund, currentSipAmount))
  }

  for (const placement of idealPlacements) {
    for (const catalogFund of placement.funds) {
      const allocation = sipMockFundAllocation(idealPercents[idealFundIndex] ?? 0)
      idealFundIndex += 1
      const idealSip = mockCountedIdealSip(catalogFund, allocation)
      pushSipMockCategoryFund(idealByCategory, catalogFund.categoryId, idealSip)
      if (placement.action === 'start') {
        continue
      }
      const recommendedSipAmount = deriveRecommendedSipAmount(totalIdealSipAmount, allocation)
      pushSipMockCategoryFund(
        includedByCategory,
        catalogFund.categoryId,
        mockCountedIncludedFund(catalogFund, countedCurrentSipAmount(placement.action, recommendedSipAmount))
      )
    }
  }

  return SIP_MOCK_COUNT_CATEGORY_ORDER.flatMap((category) => {
    const includedFunds = includedByCategory.get(category.categoryId) ?? []
    const idealSips = idealByCategory.get(category.categoryId) ?? []
    if (includedFunds.length === 0 && idealSips.length === 0) {
      return []
    }
    return [{
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      idealAllocationPercentage: sipMockCategoryAllocation(idealSips),
      includedFunds,
      idealSips
    }]
  })
}

type SipMockCountCategory = {
  categoryId: string
  categoryName: string
  fundNature: string
  funds: Array<Pick<SipMockCountFund, 'amc' | 'isin' | 'fundName'>>
}

function roundRobinSipMockCountFunds(categories: SipMockCountCategory[]): SipMockCountFund[] {
  let longestCategory = 0
  for (const category of categories) {
    if (category.funds.length > longestCategory) {
      longestCategory = category.funds.length
    }
  }

  const queue: SipMockCountFund[] = []
  for (let fundIndex = 0; fundIndex < longestCategory; fundIndex += 1) {
    for (const category of categories) {
      const catalogFund = category.funds[fundIndex]
      if (!catalogFund) {
        continue
      }
      queue.push({
        ...catalogFund,
        categoryId: category.categoryId,
        categoryName: category.categoryName,
        fundNature: category.fundNature
      })
    }
  }
  return queue
}

function sipMockIdealPercents(fundCount: number): number[] {
  if (fundCount <= 0) {
    return []
  }
  const basePercent = Math.floor(100 / fundCount)
  const extraPercent = 100 - basePercent * fundCount
  const percents: number[] = []
  for (let fundIndex = 0; fundIndex < fundCount; fundIndex += 1) {
    percents.push(fundIndex < extraPercent ? basePercent + 1 : basePercent)
  }
  return percents
}

function sipMockFundAllocation(absolute: number) {
  return {
    absolute,
    lowerLimit: absolute > 0 ? absolute - 1 : 0,
    upperLimit: absolute < 100 ? absolute + 1 : 100
  }
}

function sipMockCategoryAllocation(idealSips: SipIdealSip[]) {
  let absolute = 0
  let lowerLimit = 0
  let upperLimit = 0
  for (const idealSip of idealSips) {
    absolute += idealSip.idealAllocationPercentage?.absolute ?? 0
    lowerLimit += idealSip.idealAllocationPercentage?.lowerLimit ?? 0
    upperLimit += idealSip.idealAllocationPercentage?.upperLimit ?? 0
  }
  return {
    absolute,
    lowerLimit,
    upperLimit: Math.min(100, upperLimit)
  }
}

function countedCurrentSipAmount(
  action: 'increase' | 'decrease' | 'noAction',
  recommendedSipAmount: number
): number {
  if (action === 'noAction') {
    return recommendedSipAmount
  }
  if (action === 'increase') {
    const lowered = Math.round(recommendedSipAmount * 0.6)
    if (lowered > 0 && lowered < recommendedSipAmount) {
      return lowered
    }
    return recommendedSipAmount > 1 ? 1 : 0
  }
  return recommendedSipAmount + Math.max(2000, Math.round(recommendedSipAmount * 0.25))
}

function pushSipMockCategoryFund<Fund>(fundsByCategory: Map<string, Fund[]>, categoryId: string, fund: Fund) {
  const categoryFunds = fundsByCategory.get(categoryId) ?? []
  categoryFunds.push(fund)
  fundsByCategory.set(categoryId, categoryFunds)
}

function mockCountedIncludedFund(catalogFund: SipMockCountFund, currentSipAmount: number): SipIncludedFund {
  return mockIncludedFund(catalogFund.amc, {
    isin: catalogFund.isin,
    fundName: catalogFund.fundName,
    fundCategory: catalogFund.categoryName,
    fundNature: catalogFund.fundNature,
    ...sipAmounts(currentSipAmount)
  })
}

function mockCountedIdealSip(
  catalogFund: SipMockCountFund,
  idealAllocationPercentage: { absolute: number; lowerLimit: number; upperLimit: number }
): SipIdealSip {
  return mockIdealSip(catalogFund.amc, {
    isin: catalogFund.isin,
    fundName: catalogFund.fundName,
    fundCategory: catalogFund.categoryName,
    fundNature: catalogFund.fundNature,
    idealAllocationPercentage
  })
}

function mockIdealSip(amc: MockFundAmc, fund: Omit<SipIdealSip, 'mfLogoUrl'>): SipIdealSip {
  return {
    fundNature: 'Equity',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    taxCategory: 'EQUITY',
    powerRank: 2,
    powerRating: 'IN_FORM',
    ...fund,
    mfLogoUrl: getFundLogoPath(amc)
  }
}

function mergeSipDemoCaseFundsIntoLargeCap(category: SipPlansOverviewCategory): SipPlansOverviewCategory {
  if (category.categoryId !== 'LARGE_CAP') {
    return category
  }

  return {
    ...category,
    includedFunds: [
      ...buildSipBottomSheetDemoIncludedFunds(),
      ...buildSipBottomSheetDemoStopIncludedFunds(),
      ...(category.includedFunds ?? [])
    ],
    idealSips: [
      ...buildSipGiftFundDemoIdealSips(),
      ...buildSipBottomSheetDemoIdealSips(),
      ...(category.idealSips ?? [])
    ]
  }
}

function buildOverviewCategoriesNormal(): SipPlansOverviewCategory[] {
  return [
    mergeSipDemoCaseFundsIntoLargeCap({
      categoryId: 'LARGE_CAP',
      categoryName: 'Large Cap',
      idealAllocationPercentage: { absolute: 17.5, lowerLimit: 15.7, upperLimit: 20.6 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(50000.99099)
        }),
        mockIncludedFund(MockFundAmc.ICICI_PRUDENTIAL, {
          isin: 'INF109K01YH2',
          fundName: 'ICICI Prudential Large Cap Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(25000.99099, { external: true }),
          powerRank: 45,
          powerRating: 'OUT_OF_FORM',
          underPerformance: true
        }),
        mockIncludedFund(MockFundAmc.SBI, {
          isin: 'INF109K01INC1',
          fundName: 'SBI Bluechip Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(10000.99099, { external: true }),
          overDiversification: true,
          recommendationStatus: SipRecommendationStatus.SAME,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME
        }),
        mockIncludedFund(MockFundAmc.DSP, {
          isin: 'INF109K01DEC1',
          fundName: 'DSP Top 100 Equity Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(50000.99099, { powerup: 20000, external: 20000 }),
          overExposure: true
        }),
        mockIncludedFund(MockFundAmc.NIPPON_LIFE_INDIA, {
          isin: 'INF179K01NC1',
          fundName: 'Nippon India Large Cap Fund',
          fundCategory: 'Large Cap',
          // Exact match to ideal (2.5% of ₹6L) → no-action bucket
          ...sipAmounts(15000.99099)
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          idealAllocationPercentage: { absolute: 8.3, lowerLimit: 6.7, upperLimit: 10.8 }
        }),
        mockIdealSip(MockFundAmc.ICICI_PRUDENTIAL, {
          isin: 'INF109K01BLU1',
          fundName: 'ICICI Bluechip Fund',
          fundCategory: 'Large Cap',
          overDiversification: true,
          idealAllocationPercentage: { absolute: 5.9, lowerLimit: 4.2, upperLimit: 8.3 }
        }),
        mockIdealSip(MockFundAmc.AXIS, {
          isin: 'INF846K01EW2',
          fundName: 'Axis Bluechip Fund',
          fundCategory: 'Large Cap',
          highExposure: true,
          recommendationStatus: SipRecommendationStatus.SAME,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME,
          idealAllocationPercentage: { absolute: 4.5, lowerLimit: 3.4, upperLimit: 6.7 }
        }),
        mockIdealSip(MockFundAmc.SBI, {
          isin: 'INF109K01INC1',
          fundName: 'SBI Bluechip Fund',
          fundCategory: 'Large Cap',
          overDiversification: true,
          idealAllocationPercentage: { absolute: 4.2, lowerLimit: 3.4, upperLimit: 5.9 }
        }),
        mockIdealSip(MockFundAmc.DSP, {
          isin: 'INF109K01DEC1',
          fundName: 'DSP Top 100 Equity Fund',
          fundCategory: 'Large Cap',
          overExposure: true,
          idealAllocationPercentage: { absolute: 2.5, lowerLimit: 1.6, upperLimit: 4.2 }
        }),
        mockIdealSip(MockFundAmc.NIPPON_LIFE_INDIA, {
          isin: 'INF179K01NC1',
          fundName: 'Nippon India Large Cap Fund',
          fundCategory: 'Large Cap',
          idealAllocationPercentage: { absolute: 2.5, lowerLimit: 1.6, upperLimit: 4.2 }
        })
      ]
    }),
    {
      categoryId: 'MID_CAP',
      categoryName: 'Mid Cap',
      idealAllocationPercentage: { absolute: 12.4, lowerLimit: 10.8, upperLimit: 15.7 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.KOTAK_MAHINDRA, {
          isin: 'INF174K01LS2',
          fundName: 'Kotak Emerging Equity Fund',
          fundCategory: 'Mid Cap',
          ...sipAmounts(25000.99099, { external: true }),
          powerRank: 45,
          powerRating: 'IN_FORM',
          recommendationStatusReason:
            'We have recommended better fund in Mid Cap as per allocation strategy Although this fund is among the top funds in this category, we recommend being invested only in top ranked (1st,2nd,3rd), and hence we made this recommendation.'
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.NIPPON_LIFE_INDIA, {
          isin: 'INF959K01LX2',
          fundName: 'Nippon India Growth Fund',
          fundCategory: 'Mid Cap',
          highExposure: true,
          idealAllocationPercentage: { absolute: 12.4, lowerLimit: 8.3, upperLimit: 12.4 }
        })
      ]
    },
    {
      categoryId: 'SMALL_CAP',
      categoryName: 'Small Cap',
      idealAllocationPercentage: { absolute: 7.5, lowerLimit: 5.9, upperLimit: 10.8 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.AXIS, {
          isin: 'INF846K01SC1',
          fundName: 'Axis Small Cap Fund',
          fundCategory: 'Small Cap',
          ...sipAmounts(8000.99099),
          highExposure: true
        }),
        mockIncludedFund(MockFundAmc.MIRAE, {
          isin: 'INF397K01MR1',
          fundName: 'Mirae Asset Small Cap Fund',
          fundCategory: 'Small Cap',
          ...sipAmounts(40000.99099, { powerup: 15000, external: 15000 }),
          overExposure: true
        }),
        mockIncludedFund(MockFundAmc.SBI, {
          isin: 'INF397K01MR2',
          fundName: 'SBI Small Cap Fund',
          fundCategory: 'Small Cap',
          ...sipAmounts(25000.99099, { external: true }),
          overDiversification: true
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.AXIS, {
          isin: 'INF846K01SC1',
          fundName: 'Axis Small Cap Fund',
          fundCategory: 'Small Cap',
          highExposure: true,
          idealAllocationPercentage: { absolute: 3.4, lowerLimit: 2.6, upperLimit: 4.2 }
        }),
        mockIdealSip(MockFundAmc.MIRAE, {
          isin: 'INF397K01MR1',
          fundName: 'Mirae Asset Small Cap Fund',
          fundCategory: 'Small Cap',
          overExposure: true,
          idealAllocationPercentage: { absolute: 2.5, lowerLimit: 1.6, upperLimit: 4.2 }
        }),
        mockIdealSip(MockFundAmc.SBI, {
          isin: 'INF397K01MR2',
          fundName: 'SBI Small Cap Fund',
          fundCategory: 'Small Cap',
          overDiversification: true,
          idealAllocationPercentage: { absolute: 2.6, lowerLimit: 1.5, upperLimit: 3.4 }
        })
      ]
    },
    {
      categoryId: 'DEBT',
      categoryName: 'Debt',
      idealAllocationPercentage: { absolute: 25.5, lowerLimit: 20.6, upperLimit: 30.4 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.DSP, {
          isin: 'INF194K01DB1',
          fundName: 'DSP Short Term Fund',
          fundCategory: 'Debt',
          fundNature: 'Debt',
          ...sipAmounts(20000.99099)
        }),
        mockIncludedFund(MockFundAmc.FRANKLIN_TEMPLETON, {
          isin: 'INF090I01DB2',
          fundName: 'Franklin India Short Term Income Plan',
          fundCategory: 'Debt',
          fundNature: 'Debt',
          ...sipAmounts(10000.99099, { external: true })
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.DSP, {
          isin: 'INF194K01DB1',
          fundName: 'DSP Short Term Fund',
          fundCategory: 'Debt',
          fundNature: 'Debt',
          idealAllocationPercentage: { absolute: 15.7, lowerLimit: 12.4, upperLimit: 18.1 }
        }),
        mockIdealSip(MockFundAmc.FRANKLIN_TEMPLETON, {
          isin: 'INF090I01DB2',
          fundName: 'Franklin India Short Term Income Plan',
          fundCategory: 'Debt',
          fundNature: 'Debt',
          idealAllocationPercentage: { absolute: 10.8, lowerLimit: 8.3, upperLimit: 12.4 }
        })
      ]
    },
    {
      categoryId: 'HYBRID',
      categoryName: 'Hybrid',
      idealAllocationPercentage: { absolute: 12.5, lowerLimit: 10.8, upperLimit: 15.7 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.AXIS, {
          isin: 'INF846K01HY2',
          fundName: 'Axis Aggressive Hybrid Fund',
          fundCategory: 'Hybrid',
          fundNature: 'Hybrid',
          ...sipAmounts(18000.99099, { powerup: 10000.99099, external: 8000 })
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.HDFC, {
          isin: 'INF179K01HY1',
          fundName: 'HDFC Balanced Advantage Fund',
          fundCategory: 'Hybrid',
          fundNature: 'Hybrid',
          overDiversification: true,
          idealAllocationPercentage: { absolute: 6.7, lowerLimit: 4.2, upperLimit: 8.3 }
        }),
        mockIdealSip(MockFundAmc.AXIS, {
          isin: 'INF846K01HY2',
          fundName: 'Axis Aggressive Hybrid Fund',
          fundCategory: 'Hybrid',
          fundNature: 'Hybrid',
          idealAllocationPercentage: { absolute: 6.5, lowerLimit: 5.9, upperLimit: 8.3 }
        })
      ]
    },
    {
      categoryId: 'INTERNATIONAL',
      categoryName: 'International',
      idealAllocationPercentage: { absolute: 5.9, lowerLimit: 3.4, upperLimit: 7.5 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.NIPPON_LIFE_INDIA, {
          isin: 'INF200K01INT1',
          fundName: 'Nippon India US Equity Opportunities Fund',
          fundCategory: 'International',
          ...sipAmounts(25000.99099, { external: true }),
          overExposure: true
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.MOTILAL_OSWAL, {
          isin: 'INF200K01INT2',
          fundName: 'Motilal Oswal Nasdaq 100 Fund of Fund',
          fundCategory: 'International',
          highExposure: true,
          idealAllocationPercentage: { absolute: 5.9, lowerLimit: 3.4, upperLimit: 7.5 }
        })
      ]
    }
  ]
}

function buildSipCategoryShell(
  categoryId: string,
  categoryName: string,
  idealAllocationPercentage: { absolute: number; lowerLimit: number; upperLimit: number }
): SipPlansOverviewCategory {
  return {
    categoryId,
    categoryName,
    idealAllocationPercentage,
    includedFunds: [],
    idealSips: []
  }
}

const MOCK_SIP_CATEGORY_SHELLS: SipPlansOverviewCategory[] = [
  buildSipCategoryShell('MID_CAP', 'Mid Cap', { absolute: 12.4, lowerLimit: 10.8, upperLimit: 15.7 }),
  buildSipCategoryShell('SMALL_CAP', 'Small Cap', { absolute: 7.5, lowerLimit: 5.9, upperLimit: 10.8 }),
  buildSipCategoryShell('DEBT', 'Debt', { absolute: 25.5, lowerLimit: 20.6, upperLimit: 30.4 }),
  buildSipCategoryShell('HYBRID', 'Hybrid', { absolute: 12.5, lowerLimit: 10.8, upperLimit: 15.7 }),
  buildSipCategoryShell('INTERNATIONAL', 'International', { absolute: 5.9, lowerLimit: 3.4, upperLimit: 7.5 })
]

function buildOverviewCategoriesWithTags(): SipPlansOverviewCategory[] {
  return [
    mergeSipDemoCaseFundsIntoLargeCap({
      categoryId: 'LARGE_CAP',
      categoryName: 'Large Cap',
      idealAllocationPercentage: { absolute: 17.5, lowerLimit: 15.7, upperLimit: 20.6 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.ICICI_PRUDENTIAL, {
          isin: 'INF109K01YH2',
          fundName: 'ICICI Prudential Large Cap Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(25000.99099, { external: true }),
          powerRank: 45,
          powerRating: 'OUT_OF_FORM',
          underPerformance: true,
          highExposure: true,
          recommendationStatus: SipRecommendationStatus.UPDATED,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.UPDATED_STOP
        }),
        mockIncludedFund(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(55000.99099),
          recommendationStatus: SipRecommendationStatus.UPDATED,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.UPDATED_DECREASE
        }),
        mockIncludedFund(MockFundAmc.SBI, {
          isin: 'INF109K01INC1',
          fundName: 'SBI Bluechip Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(10000.99099, { powerup: 6000, external: 4000.99099 }),
          overDiversification: true,
          recommendationStatus: SipRecommendationStatus.SAME,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.AXIS, {
          isin: 'INF846K01EW2',
          fundName: 'Axis Bluechip Fund',
          fundCategory: 'Large Cap',
          highExposure: true,
          recommendationStatus: SipRecommendationStatus.NEW,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.NEW,
          idealAllocationPercentage: { absolute: 5.9, lowerLimit: 4.2, upperLimit: 8.3 }
        }),
        mockIdealSip(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          recommendationStatus: SipRecommendationStatus.SAME,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME,
          idealAllocationPercentage: { absolute: 8.3, lowerLimit: 6.7, upperLimit: 10.8 }
        }),
        mockIdealSip(MockFundAmc.SBI, {
          isin: 'INF109K01INC1',
          fundName: 'SBI Bluechip Fund',
          fundCategory: 'Large Cap',
          overDiversification: true,
          recommendationStatus: SipRecommendationStatus.UPDATED,
          recommendationStatusDisplayText: MOCK_SIP_RECOMMENDATION_STATUS_DISPLAY_TEXT.UPDATED_INCREASE,
          idealAllocationPercentage: { absolute: 4.2, lowerLimit: 3.4, upperLimit: 5.9 }
        })
      ]
    }),
    ...MOCK_SIP_CATEGORY_SHELLS
  ]
}

function buildOverviewCategoriesUnderReviewOnly(): SipPlansOverviewCategory[] {
  return [
    {
      categoryId: 'LARGE_CAP',
      categoryName: 'Large Cap',
      idealAllocationPercentage: { absolute: 17.5, lowerLimit: 15.7, upperLimit: 20.6 },
      includedFunds: [
        mockIncludedFund(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          ...sipAmounts(48000.99099)
        })
      ],
      idealSips: [
        mockIdealSip(MockFundAmc.HDFC, {
          isin: 'INF179K01YH8',
          fundName: 'HDFC Large Cap Fund',
          fundCategory: 'Large Cap',
          idealAllocationPercentage: { absolute: 8.3, lowerLimit: 6.7, upperLimit: 10.8 }
        })
      ]
    },
    ...MOCK_SIP_CATEGORY_SHELLS
  ]
}

function getOverviewCategoriesForScenario(scenario: SipPlansMockScenario): SipPlansOverviewCategory[] {
  if (scenario === SipPlansMockScenario.WITH_TAGS) {
    return buildOverviewCategoriesWithTags()
  }
  if (scenario === SipPlansMockScenario.UNDER_REVIEW_ONLY) {
    return buildOverviewCategoriesUnderReviewOnly()
  }
  return buildOverviewCategoriesNormal()
}

function getMockPlanCountForScenario(scenario: SipPlansMockScenario): number {
  if (scenario === SipPlansMockScenario.WITH_TAGS) {
    return 2
  }
  if (scenario === SipPlansMockScenario.EMPTY) {
    return 0
  }
  return 1
}

function getMockPlanSnapshotDateForScenario(scenario: SipPlansMockScenario): number {
  if (scenario === SipPlansMockScenario.WITH_TAGS) {
    return Date.now() - 2 * 24 * 60 * 60 * 1000
  }
  return Date.now()
}

function countIncludedFunds(categories: SipPlansOverviewCategory[]): number {
  let count = 0
  for (const category of categories) {
    count += category?.includedFunds?.length ?? 0
  }
  return count
}

function countIdealSips(categories: SipPlansOverviewCategory[]): number {
  let count = 0
  for (const category of categories) {
    count += category?.idealSips?.length ?? 0
  }
  return count
}

/** Pair every ideal SIP with a current amount equal to its recommended amount, so nothing is start/stop/increase/decrease. */
function applySipCategoriesTopShape(
  categories: SipPlansOverviewCategory[],
  totalIdealSipAmount: number
): SipPlansOverviewCategory[] {
  return categories.map((category) => {
    const includedFundByIsin = new Map(
      (category?.includedFunds ?? []).map((includedFund) => [includedFund?.isin ?? '', includedFund])
    )
    const idealSips: SipIdealSip[] = []
    const includedFunds: SipIncludedFund[] = []

    for (const idealSip of category?.idealSips ?? []) {
      const recommendedSipAmount = deriveRecommendedSipAmount(totalIdealSipAmount, idealSip?.idealAllocationPercentage)
      if (recommendedSipAmount <= 0) {
        continue
      }
      idealSips.push(idealSip)
      const existingIncludedFund = includedFundByIsin.get(idealSip?.isin ?? '')
      if (existingIncludedFund) {
        includedFunds.push({ ...existingIncludedFund, ...sipAmounts(recommendedSipAmount) })
      } else {
        includedFunds.push({ ...idealSip, ...sipAmounts(recommendedSipAmount) })
      }
    }

    return { ...category, idealSips, includedFunds }
  })
}

function sumCurrentSipAmounts(categories: SipPlansOverviewCategory[]): number {
  let total = 0
  for (const category of categories) {
    for (const includedFund of category?.includedFunds ?? []) {
      total += includedFund?.currentSipAmount ?? 0
    }
  }
  return total
}

export function mockSipPlansOverview(
  scenario: SipPlansMockScenario = SipPlansMockScenario.NORMAL,
  planGate: SipPlanGateMockConfig = DEFAULT_SIP_PLAN_GATE
): SipPlansOverviewResponse {
  const { planStatus, isPlanSubscriptionEligible, daysToPlanDrop, isTopShape, yourPlanCounts } = planGate
  const listScenario = resolveListScenarioForPlanStatus(planStatus, scenario)

  if (listScenario === SipPlansMockScenario.EMPTY) {
    return {
      planType: SipPlansPlanType.SIP,
      timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
      isPlanEnabled: true,
      isPlanSubscriptionEligible,
      planStatus,
      planCount: 0,
      initiateSipCta: MOCK_INITIATE_SIP_CTA,
      manageSipCta: MOCK_MANAGE_SIP_CTA,
      strategyCta: MOCK_STRATEGY_CTA,
      resetAllocation: MOCK_RESET_ALLOCATION_CTA,
      editSip: MOCK_EDIT_SIP_CTA,
      strategyLandingCta: MOCK_STRATEGY_LANDING_CTA,
      slotBottomsheetCta: MOCK_SLOT_BOTTOMSHEET_CTA,
      includedFundCount: 0,
      idealFundCount: 0,
      totalCurrentSipAmount: 0,
      totalIdealSipAmount: 0,
      minSipValue: 1000,
      maxSipValue: 10_000_000,
      planSnapshotDate: Date.now(),
      planDurationInMonths: 1,
      riskProfile: AllocationRiskProfile.AGGRESSIVE,
      categories: []
    }
  }

  const defaultTotalIdealSipAmount = 600000.99099
  const totalIdealSipAmount = mockPersistedTotalIdealSipAmount ?? defaultTotalIdealSipAmount
  const overviewCategories =
    listScenario === SipPlansMockScenario.NORMAL
      ? buildSipYourPlanCountCategories(totalIdealSipAmount, yourPlanCounts)
      : getOverviewCategoriesForScenario(listScenario)
  const categories = isTopShape
    ? applySipCategoriesTopShape(overviewCategories, totalIdealSipAmount)
    : overviewCategories

  return {
    planType: SipPlansPlanType.SIP,
    timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
    isPlanEnabled: true,
    isPlanSubscriptionEligible,
    planStatus,
    planCount: getMockPlanCountForScenario(listScenario),
    initiateSipCta: MOCK_INITIATE_SIP_CTA,
    manageSipCta: MOCK_MANAGE_SIP_CTA,
    strategyCta: MOCK_STRATEGY_CTA,
    resetAllocation: MOCK_RESET_ALLOCATION_CTA,
    editSip: MOCK_EDIT_SIP_CTA,
    strategyLandingCta: MOCK_STRATEGY_LANDING_CTA,
    slotBottomsheetCta: MOCK_SLOT_BOTTOMSHEET_CTA,
    includedFundCount: countIncludedFunds(categories),
    idealFundCount: countIdealSips(categories),
    totalCurrentSipAmount: sumCurrentSipAmounts(categories),
    totalIdealSipAmount,
    minSipValue: 1000,
    maxSipValue: 10_000_000,
    planSnapshotDate: getMockPlanSnapshotDateForScenario(listScenario),
    planDurationInMonths: 6,
    riskProfile: AllocationRiskProfile.AGGRESSIVE,
    categories
  }
}

export function mockUpdateIdealSipAmount(
  totalIdealSipAmount: number,
  scenario: SipPlansMockScenario = SipPlansMockScenario.NORMAL,
  planGate: SipPlanGateMockConfig = DEFAULT_SIP_PLAN_GATE
): SipPlansOverviewResponse {
  mockPersistedTotalIdealSipAmount = totalIdealSipAmount
  return mockSipPlansOverview(scenario, planGate)
}

// --- bottom-sheet demo funds (shared with SIP sheet mocks) ---

export const SIP_BOTTOM_SHEET_DEMO_ISINS = {
  INCREASE: 'INF846K01INC8',
  DECREASE: 'INF846K01DEC8',
  /** Synced with PLAN_MOCK_FUNDS.sipStartDisabled → sipDisabled validations mock. */
  START: PLAN_MOCK_FUNDS.sipStartDisabled.isin,
  /** Synced with PLAN_MOCK_FUNDS.buyPpfasGiftFundEnquiry → gift-fund enquiry sheet (Start SIP). */
  GIFT_FUND: PLAN_MOCK_FUNDS.buyPpfasGiftFundEnquiry.isin,
  STOP_CASE_1_BAD_FUND_NEED_EXPOSURE: 'INF846K01STP2',
  STOP_CASE_2_BAD_FUND_NO_EXPOSURE: 'INF846K01STP1',
  STOP_CASE_3_GOOD_FUND_NO_EXPOSURE: 'INF846K01STP3',
  STOP_CASE_4_IN_FORM_NEED_EXPOSURE: 'INF846K01STP4',
  STOP_CASE_5_ON_TRACK_NEED_EXPOSURE: 'INF846K01STP5'
} as const

/** Overview stop funds that are not bottom-sheet demo ISINs. */
export const SIP_OVERVIEW_STOP_ISINS = {
  LARGE_CAP_ICICI: 'INF109K01YH2',
  MID_CAP_KOTAK: 'INF174K01LS2',
  INTERNATIONAL_NIPPON: 'INF200K01INT1'
} as const

export const SIP_MOCK_TOTAL_CATEGORY_COUNT = 29

export const SIP_BOTTOM_SHEET_DEMO_PLAN_SNAPSHOT_DATE = Date.UTC(2025, 7, 24)
export const SIP_BOTTOM_SHEET_DEMO_DERIVED_CATEGORY = 'Large Cap'

export const SIP_DEMO_BAD_SCORING_DATA: SipPlanScoringData = {
  recencyScore: 96,
  consistencyScore: 96,
  volatilityScore: 46,
  maxScore: 100,
  consistencyScorePerformanceTag: 'Bad',
  recencyScorePerformanceTag: 'Bad',
  volatilityScorePerformanceTag: 'Average'
}

export const SIP_DEMO_START_SCORING_DATA: SipPlanScoringData = {
  recencyScore: 82,
  consistencyScore: 96,
  volatilityScore: 96,
  maxScore: 100,
  consistencyScorePerformanceTag: 'Great',
  recencyScorePerformanceTag: 'Good',
  volatilityScorePerformanceTag: 'Great'
}

/** Ideal bottom-sheet scoring — used for every ideal SIP in mocks. */
export const SIP_IDEAL_SCORING_DATA: SipPlanScoringData = {
  recencyScore: 82,
  consistencyScore: 96,
  volatilityScore: 96,
  maxScore: 100,
  consistencyScorePerformanceTag: 'GREAT',
  recencyScorePerformanceTag: 'GOOD',
  volatilityScorePerformanceTag: 'GREAT'
}

export type SipStopRecommendationMock = {
  sipRecommendationCase: SipCase
  scoringData?: SipPlanScoringData
  includesCategoryIdealAllocation?: boolean
  derivedCategory?: string
}

/** Bottom-sheet stop response per ISIN — every fund in overview stop buckets must be listed here. */
export const SIP_STOP_RECOMMENDATION_MOCK_BY_ISIN: Record<string, SipStopRecommendationMock> = {
  [SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_1_BAD_FUND_NEED_EXPOSURE]: {
    sipRecommendationCase: SipCase.STOP_UNDER_PERFORMING_FUND_WITH_EXPOSURE,
    scoringData: SIP_DEMO_BAD_SCORING_DATA,
    derivedCategory: SIP_BOTTOM_SHEET_DEMO_DERIVED_CATEGORY
  },
  [SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_2_BAD_FUND_NO_EXPOSURE]: {
    sipRecommendationCase: SipCase.STOP_UNDER_PERFORMING_FUND_WITHOUT_EXPOSURE,
    scoringData: SIP_DEMO_BAD_SCORING_DATA,
    includesCategoryIdealAllocation: true,
    derivedCategory: SIP_BOTTOM_SHEET_DEMO_DERIVED_CATEGORY
  },
  [SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_3_GOOD_FUND_NO_EXPOSURE]: {
    sipRecommendationCase: SipCase.STOP_WELL_PERFORMING_FUND_WITHOUT_EXPOSURE,
    includesCategoryIdealAllocation: true
  },
  [SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_4_IN_FORM_NEED_EXPOSURE]: {
    sipRecommendationCase: SipCase.STOP_IN_FORM_FUND_WITH_EXPOSURE
  },
  [SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_5_ON_TRACK_NEED_EXPOSURE]: {
    sipRecommendationCase: SipCase.STOP_ON_TRACK_FUND_WITH_EXPOSURE
  },
  [SIP_OVERVIEW_STOP_ISINS.LARGE_CAP_ICICI]: {
    sipRecommendationCase: SipCase.STOP_UNDER_PERFORMING_FUND_WITH_EXPOSURE,
    scoringData: SIP_DEMO_BAD_SCORING_DATA,
    derivedCategory: 'Large Cap'
  },
  [SIP_OVERVIEW_STOP_ISINS.MID_CAP_KOTAK]: {
    sipRecommendationCase: SipCase.STOP_IN_FORM_FUND_WITH_EXPOSURE
  },
  [SIP_OVERVIEW_STOP_ISINS.INTERNATIONAL_NIPPON]: {
    sipRecommendationCase: SipCase.STOP_IN_FORM_FUND_WITH_EXPOSURE
  }
}

export function isSipDemoCaseIsin(isin: string): boolean {
  return Object.values(SIP_BOTTOM_SHEET_DEMO_ISINS).includes(
    isin as (typeof SIP_BOTTOM_SHEET_DEMO_ISINS)[keyof typeof SIP_BOTTOM_SHEET_DEMO_ISINS]
  )
}

const DEMO_FUND_BASE = {
  fundName: 'HDFC Bluechip Fund – Growth',
  mfLogoUrl: getFundLogoPath(MockFundAmc.HDFC),
  fundNature: 'Equity',
  fundCategory: 'Large Cap',
  purchaseMode: 'REGULAR',
  distributionStatus: 'GROWTH',
  taxCategory: 'EQUITY',
  powerRank: 1,
  powerRating: 'IN_FORM',
  recommendationStatus: null,
  recommendationStatusReason: null,
  recommendationStatusDisplayText: null,
  underPerformance: false,
  overDiversification: false
} as const

/** PPFAS GIFT fund — ideal-only → Start SIP (same ISIN as lumpsum buy). */
export function buildSipGiftFundDemoIdealSips(): SipIdealSip[] {
  return [
    {
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.GIFT_FUND,
      fundName: PLAN_MOCK_FUNDS.buyPpfasGiftFundEnquiry.fundName,
      mfLogoUrl: getFundLogoPath(PLAN_MOCK_FUNDS.buyPpfasGiftFundEnquiry.amc),
      fundNature: 'Equity',
      fundCategory: 'Large Cap',
      purchaseMode: 'DIRECT',
      distributionStatus: 'GROWTH',
      taxCategory: 'EQUITY',
      powerRank: 1,
      powerRating: 'IN_FORM',
      recommendationStatus: null,
      recommendationStatusReason: null,
      recommendationStatusDisplayText: null,
      underPerformance: false,
      overDiversification: false,
      highExposure: true,
      overExposure: false,
      idealAllocationPercentage: { absolute: 5, lowerLimit: 3.4, upperLimit: 6.7 }
    }
  ]
}

export function buildSipBottomSheetDemoIncludedFunds(): SipIncludedFund[] {
  return [
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.INCREASE,
      ...sipAmounts(25000.99099, { external: true }),
      highExposure: true,
      overExposure: false
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.DECREASE,
      ...sipAmounts(25000.99099, { powerup: 8000.99099, external: 10000, infinite: 7000 }),
      highExposure: false,
      overExposure: true
    }
  ]
}

export function buildSipBottomSheetDemoStopIncludedFunds(): SipIncludedFund[] {
  return [
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_2_BAD_FUND_NO_EXPOSURE,
      ...sipAmounts(25000.99099, { external: true }),
      powerRank: 45,
      powerRating: 'OUT_OF_FORM',
      underPerformance: true,
      overExposure: true
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_1_BAD_FUND_NEED_EXPOSURE,
      ...sipAmounts(25000.99099),
      powerRank: 45,
      powerRating: 'OFF_TRACK',
      underPerformance: true,
      overExposure: false
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_3_GOOD_FUND_NO_EXPOSURE,
      ...sipAmounts(25000.99099, { powerup: 10000.99099, external: 8000, infinite: 7000 }),
      powerRank: 1,
      powerRating: 'IN_FORM',
      underPerformance: false,
      overExposure: true
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_4_IN_FORM_NEED_EXPOSURE,
      ...sipAmounts(25000.99099),
      powerRank: 4,
      powerRating: 'IN_FORM',
      underPerformance: false,
      overExposure: false
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.STOP_CASE_5_ON_TRACK_NEED_EXPOSURE,
      ...sipAmounts(25000.99099, { infinite: true }),
      powerRank: 12,
      powerRating: 'ON_TRACK',
      underPerformance: false,
      overExposure: false
    }
  ]
}

export function buildSipBottomSheetDemoIdealSips(): SipIdealSip[] {
  return [
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.INCREASE,
      highExposure: true,
      overExposure: false,
      idealAllocationPercentage: { absolute: 5.83, lowerLimit: 4.2, upperLimit: 8.3 }
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.DECREASE,
      highExposure: false,
      overExposure: true,
      idealAllocationPercentage: { absolute: 2.5, lowerLimit: 1.6, upperLimit: 4.2 }
    },
    {
      ...DEMO_FUND_BASE,
      isin: SIP_BOTTOM_SHEET_DEMO_ISINS.START,
      highExposure: true,
      overExposure: false,
      idealAllocationPercentage: { absolute: 4.17, lowerLimit: 3.4, upperLimit: 6.7 }
    }
  ]
}
